import React from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import ScrollToTop from './components/ScrollToTop';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { trackPageView, identify } from './lib/analytics';
import Home from './pages/Home';
import About from './pages/About';
import FeaturesPage from './pages/FeaturesPage';
import HowItWorksPage from './pages/HowItWorksPage';
import PricingPage from './pages/PricingPage';
import FAQPage from './pages/FAQPage';
import RiskDisclosurePage from './pages/RiskDisclosurePage';
import ContactPage from './pages/ContactPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfUse from './pages/TermsOfUse';
import Signup from './pages/Signup';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import AlpacaSuccess from './pages/AlpacaSuccess';
import AlpacaError from './pages/AlpacaError';
import AdminHome from './pages/AdminHome';
import AdminUsers from './pages/AdminUsers';
import AdminSignals from './pages/AdminSignals';
import AdminExecutions from './pages/AdminExecutions';
import AdminTrades from './pages/AdminTrades';
import AdminBilling from './pages/AdminBilling';
import AdminContacts from './pages/AdminContacts';
import AdminEmail from './pages/AdminEmail';
import AdminAnalytics from './pages/AdminAnalytics';
import AuthCallback from './pages/AuthCallback';
import InviteAccept from './pages/InviteAccept';
import BillingSuccess from './pages/BillingSuccess';
import { Toaster } from './components/ui/sonner';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function AppRouter() {
  const location = useLocation();
  const { user } = useAuth();

  // SPA page_view tracking on every route change.
  React.useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);

  // Identify logged-in user to GA so we can segment events by role.
  React.useEffect(() => {
    if (user?.userId) {
      identify(user.userId, {
        role: user.isAdmin ? 'admin' : 'user',
        account_status: user.accountStatus || 'beta',
        alpaca_connected: !!user.alpacaConnected,
      });
    }
  }, [user]);

  // Detect session_id synchronously during render (not in useEffect) — prevents race conditions.
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/about" element={<About />} />
      <Route path="/features" element={<FeaturesPage />} />
      <Route path="/how-it-works" element={<HowItWorksPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/risk-disclosure" element={<RiskDisclosurePage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-of-use" element={<TermsOfUse />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/login" element={<Login />} />
      <Route path="/dashboard" element={<Dashboard />} />
      <Route path="/alpaca/success" element={<AlpacaSuccess />} />
      <Route path="/alpaca/error" element={<AlpacaError />} />
      <Route path="/admin" element={<AdminHome />} />
      <Route path="/admin/users" element={<AdminUsers />} />
      <Route path="/admin/signals" element={<AdminSignals />} />
      <Route path="/admin/executions" element={<AdminExecutions />} />
      <Route path="/admin/trades" element={<AdminTrades />} />
      <Route path="/admin/billing" element={<AdminBilling />} />
      <Route path="/admin/contacts" element={<AdminContacts />} />
      <Route path="/admin/email" element={<AdminEmail />} />
      <Route path="/admin/analytics" element={<AdminAnalytics />} />
      <Route path="/invite" element={<InviteAccept />} />
      <Route path="/billing/success" element={<BillingSuccess />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ScrollToTop />
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
      <Toaster theme="dark" position="bottom-right" />
    </div>
  );
}

export default App;
